import { useEffect, useState } from "react";
import { Save, FileJson, Replace as ReplaceIcon } from "lucide-react";
import { toast } from "sonner";
import { SectionCard } from "@/components/SectionCard";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface Props {
  title: string;
  /** Sub-Label rechts vom Titel, z.B. "admin-only" oder "owner-self". */
  modeLabel?: string;
  /** Aktuell gespeicherter JSON-Inhalt aus Master. */
  value: Record<string, unknown>;
  isPending: boolean;
  /** Wenn gesetzt: erlaubt destruktiven Replace-Pfad. Bekommt das aktuell
   *  geparste Textarea-JSON (nicht Server-Daten). */
  onReplace?: (payload: Record<string, unknown>) => void;
  /** Save (merge) mit aktuellem Textarea-JSON. */
  onSave: (payload: Record<string, unknown>) => void;
}

export function ConfigSection({ title, modeLabel, value, isPending, onReplace, onSave }: Props) {
  const initial = JSON.stringify(value ?? {}, null, 2);
  const [text, setText] = useState(initial);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    setText(initial);
    setParseError(null);
  }, [initial]);

  const dirty = text !== initial;

  function tryParse(): Record<string, unknown> | null {
    try {
      const trimmed = text.trim();
      const parsed = trimmed.length === 0 ? {} : JSON.parse(trimmed);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        setParseError("muss ein JSON-Objekt sein");
        return null;
      }
      setParseError(null);
      return parsed as Record<string, unknown>;
    } catch (e) {
      setParseError(`Ungültiges JSON: ${(e as Error).message}`);
      return null;
    }
  }

  function handleFormat() {
    const parsed = tryParse();
    if (!parsed) return;
    setText(JSON.stringify(parsed, null, 2));
    toast.success("JSON formatiert");
  }

  function handleSave() {
    const parsed = tryParse();
    if (!parsed) return;
    onSave(parsed);
  }

  function handleReplace() {
    if (!onReplace) return;
    const parsed = tryParse();
    if (!parsed) return;
    onReplace(parsed);
  }

  const isEmpty = Object.keys(value ?? {}).length === 0;
  const hasInvalidJson = !!parseError;

  return (
    <SectionCard
      testId={`config-section-${title.toLowerCase()}`}
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          {title}
          {modeLabel && (
            <Badge variant="outline" className="text-[10px] uppercase">
              {modeLabel}
            </Badge>
          )}
          {isEmpty && !dirty && <span className="text-xs font-normal text-text3">(keine Settings)</span>}
        </span>
      }
    >
      <div className="space-y-2">
        <Textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (parseError) setParseError(null);
          }}
          rows={12}
          className="font-mono text-xs"
          data-testid={`config-textarea-${title.toLowerCase()}`}
          spellCheck={false}
        />
        {parseError && (
          <div className="text-xs text-danger" data-testid={`config-error-${title.toLowerCase()}`}>
            {parseError}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleFormat}
            disabled={isPending}
            data-testid={`config-format-${title.toLowerCase()}`}
          >
            <FileJson className="h-4 w-4" />
            Format JSON
          </Button>
          {onReplace && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleReplace}
              disabled={hasInvalidJson || isPending}
              data-testid={`config-replace-${title.toLowerCase()}`}
              title="Ersetzt die komplette Ebene mit dem aktuellen Textarea-Inhalt"
            >
              <ReplaceIcon className="h-4 w-4" />
              Replace All
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || hasInvalidJson || isPending}
            data-testid={`config-save-${title.toLowerCase()}`}
          >
            <Save className="h-4 w-4" />
            {isPending ? "..." : "Save (merge)"}
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}
